import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  GetIdentityVerificationsResponse,
  GetProfileVerificationsResponse,
  IdentityVerification,
  IdentityVerificationsAPI,
  MessageResponse,
  ProfileVerification,
  ProfileVerificationsAPI,
} from "../../../api/verificationsV2";
import { RootState } from "../../../app/store";

export const fetchIdentityVerifications = createAsyncThunk<
  GetIdentityVerificationsResponse,
  string,
  { state: RootState; rejectValue: string }
>(
  "verifications/fetchIdentityVerifications",
  async (caseId, { rejectWithValue }) => {
    try {
      return await IdentityVerificationsAPI.get(caseId);
    } catch {
      return rejectWithValue("Failed to fetch identity verifications");
    }
  },
  {
    condition: (caseId, { getState }) => {
      const { identityStatus, identityCaseId } = getState().verifications;

      if (identityStatus === "loading") return false;
      if (identityStatus === "success" && identityCaseId === caseId) {
        return false;
      }

      return true;
    },
  },
);

export const fetchProfileVerifications = createAsyncThunk<
  GetProfileVerificationsResponse,
  string,
  { state: RootState; rejectValue: string }
>(
  "verifications/fetchProfileVerifications",
  async (caseId, { rejectWithValue }) => {
    try {
      return await ProfileVerificationsAPI.get(caseId);
    } catch {
      return rejectWithValue("Failed to fetch profile verifications");
    }
  },
  {
    condition: (caseId, { getState }) => {
      const { profileStatus, profileCaseId } = getState().verifications;

      if (profileStatus === "loading") return false;
      if (profileStatus === "success" && profileCaseId === caseId) return false;

      return true;
    },
  },
);

export const upsertIdentityVerifications = createAsyncThunk<
  MessageResponse,
  { caseId: string; verifications: IdentityVerification[] },
  { state: RootState; rejectValue: string }
>(
  "verifications/upsertIdentityVerifications",
  async ({ caseId, verifications }, { rejectWithValue }) => {
    try {
      return await IdentityVerificationsAPI.upsert(caseId, { verifications });
    } catch {
      return rejectWithValue("Failed to save identity verifications");
    }
  },
);

export const upsertProfileVerifications = createAsyncThunk<
  MessageResponse,
  { caseId: string; verifications: ProfileVerification[] },
  { state: RootState; rejectValue: string }
>(
  "verifications/upsertProfileVerifications",
  async ({ caseId, verifications }, { rejectWithValue }) => {
    try {
      return await ProfileVerificationsAPI.upsert(caseId, { verifications });
    } catch {
      return rejectWithValue("Failed to save profile verifications");
    }
  },
);
